import { ApiClientProvider, createApiClient } from '@portals-platform/api-client';
import { BrandAuth0Provider, useAuth, withAuthAxios } from '@portals-platform/auth';
import type { ApiConfig, BrandConfig } from '@portals-platform/brand-kit';
import { I18nProvider } from '@portals-platform/i18n';
import { Skeleton } from '@portals-platform/ui';
import { type ReactNode, useMemo, useRef } from 'react';
import { BrandConfigProvider } from './brand-config-context.js';
import { router } from './router.js';

interface ProvidersProps {
  config: BrandConfig;
  children: ReactNode;
}

function AuthBridgeProvider({
  apiConfig,
  children,
}: {
  apiConfig: ApiConfig;
  children: ReactNode;
}) {
  const { getAccessToken } = useAuth();
  const getTokenRef = useRef(getAccessToken);
  getTokenRef.current = getAccessToken;

  const axiosInstance = useMemo(() => {
    const client = createApiClient(apiConfig);
    withAuthAxios(client, () => getTokenRef.current());
    return client;
  }, [apiConfig]);

  return (
    <ApiClientProvider config={apiConfig} axiosInstance={axiosInstance}>
      {children}
    </ApiClientProvider>
  );
}

export function Providers({ config, children }: ProvidersProps) {
  return (
    <BrandConfigProvider config={config}>
      <I18nProvider config={config.i18n} fallback={<Skeleton className="h-screen w-full" />}>
        {config.auth ? (
          <BrandAuth0Provider
            config={config.auth}
            onRedirectCallback={(appState) => {
              void router.navigate({
                to: (appState?.returnTo as string | undefined) ?? '/',
              });
            }}
          >
            <AuthBridgeProvider apiConfig={config.api}>{children}</AuthBridgeProvider>
          </BrandAuth0Provider>
        ) : (
          <ApiClientProvider config={config.api}>{children}</ApiClientProvider>
        )}
      </I18nProvider>
    </BrandConfigProvider>
  );
}
